/**
 * InvoiceIntakeDashboard — "Upload Invoices" power dashboard.
 *
 * Menu (big iconised buttons):
 *   1. Upload invoices to a specific project and task — pick ONE target,
 *      then drop one or many files; every file goes there.
 *   2. Upload one or more invoices to different projects and tasks — drop
 *      files first, then choose a target per file (recently used targets
 *      are offered first so several can go to the same place in one click).
 *
 * Target search: one box searching company names, project titles and task
 * titles; it runs 450 ms after typing pauses, never on every keystroke.
 *
 * Each dropped file uploads immediately; the server extracts text (PDF,
 * DOCX, CSV, TXT — no AI) and parses supplier, VAT number, invoice number,
 * dates, net/VAT/gross and line items. Images (scans) are kept for manual
 * entry. Clicking a file opens a full-screen review: the document on the
 * left, the interpreted fields on the right — edit anything, then APPROVE
 * to create the received invoice with its line items and attach the
 * document to the project. Nothing reaches the invoice ledger until then.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Input, Select, Badge, HStack, VStack, IconButton, Spinner, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  FormControl, FormLabel, Table, Thead, Tbody, Tr, Th, Td, Tooltip, Divider, Image, Switch, Tag,
} from '@chakra-ui/react';
import { FiUpload, FiFolder, FiEdit2, FiLayers, FiSearch, FiArrowLeft, FiFileText, FiCheckCircle, FiAlertCircle,
  FiTrash2, FiRefreshCw, FiEdit3, FiPlus, FiX, FiImage, FiClock, FiInbox, FiLink, FiUser, FiSmartphone, FiCamera } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useNav } from '../nav';

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,.bmp,.heic,.docx,.csv,.txt';
const fmtMoney = (v) => (v == null || v === '' || isNaN(v)) ? '—' : Number(v).toFixed(2);
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token') || '';
const fileUrl = (id) => `${(api.defaults && api.defaults.baseURL) || ''}/invoice-intake/${id}/file?token=${encodeURIComponent(token())}`;

function useDebounce(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// ── big menu button ───────────────────────────────────────────────────────
function MenuTile({ icon: Icon, secondary: Secondary, title, text, onClick }) {
  return (
    <Box as="button" onClick={onClick} textAlign="left" bg="white" border="2px solid" borderColor="gray.200" borderRadius="xl" p={6} w="100%" maxW="420px"
      _hover={{ borderColor: 'teal.400', boxShadow: 'md' }} transition="all .15s">
      <HStack spacing={5} align="center">
        <Box position="relative" w="64px" h="64px" flexShrink={0}>
          <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" color="teal.500"><Icon size={44} /></Box>
          <Box position="absolute" right="-6px" bottom="-6px" bg="white" borderRadius="full" p="3px" color="orange.500"><Secondary size={24} /></Box>
        </Box>
        <Box>
          <Text fontWeight="700" fontSize="md">{title}</Text>
          <Text fontSize="sm" color="gray.600" mt={1}>{text}</Text>
        </Box>
      </HStack>
    </Box>
  );
}

// ── target search ─────────────────────────────────────────────────────────
function TargetSearch({ onPick, recent = [], compact = false, autoFocus = true }) {
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 450);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (dq.trim().length < 2) { setRows([]); return; }
    let live = true; setBusy(true);
    api.get(`/invoice-intake/search?q=${encodeURIComponent(dq.trim())}`).then(r => { if (live) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setRows([]); }).finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [dq]);
  return (
    <Box>
      <HStack>
        <Box color="gray.500"><FiSearch /></Box>
        <Input autoFocus={autoFocus} size={compact ? 'sm' : 'md'} placeholder="Type a company, project or task name…" value={q} onChange={e => setQ(e.target.value)} />
        {busy && <Spinner size="sm" />}
      </HStack>
      {recent.length > 0 && !q && (
        <Box mt={3}>
          <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}><FiClock style={{ display: 'inline' }} /> Recently used</Text>
          <VStack align="stretch" spacing={1}>
            {recent.map((r, i) => (
              <Box key={i} as="button" textAlign="left" p={2} borderRadius="md" bg="teal.50" _hover={{ bg: 'teal.100' }} onClick={() => onPick(r)}>
                <Text fontSize="sm" fontWeight="600">{r.project_name}{r.task_title ? ` › ${r.task_title}` : ''}</Text>
                {r.company_names && <Text fontSize="xs" color="gray.600">{r.company_names}</Text>}
              </Box>
            ))}
          </VStack>
        </Box>
      )}
      {rows.length > 0 && (
        <VStack align="stretch" spacing={1} mt={3} maxH="320px" overflowY="auto">
          {rows.map(r => (
            <Box key={r.task_id} as="button" textAlign="left" p={2} borderRadius="md" border="1px solid" borderColor="gray.200" _hover={{ bg: 'gray.50', borderColor: 'teal.300' }} onClick={() => onPick(r)}>
              <Text fontSize="sm" fontWeight="600">{r.task_title}</Text>
              <Text fontSize="xs" color="gray.600">{r.project_name}{r.company_names ? ` · ${r.company_names}` : ''}</Text>
            </Box>
          ))}
        </VStack>
      )}
      {!busy && dq.trim().length >= 2 && rows.length === 0 && <Text fontSize="xs" color="gray.500" mt={2}>No matching tasks.</Text>}
    </Box>
  );
}

// ── drop zone ─────────────────────────────────────────────────────────────
function DropZone({ onFiles, disabled }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef();
  return (
    <Box border="2px dashed" borderColor={over ? 'teal.400' : 'gray.300'} bg={over ? 'teal.50' : 'gray.50'} borderRadius="xl" p={8} textAlign="center"
      opacity={disabled ? 0.5 : 1}
      onDragOver={e => { e.preventDefault(); if (!disabled) setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); if (!disabled && e.dataTransfer.files.length) onFiles(Array.from(e.dataTransfer.files)); }}>
      <Box color="teal.500" mb={2}><FiUpload size={36} style={{ display: 'inline' }} /></Box>
      <Text fontWeight="600">Drag invoices here</Text>
      <Text fontSize="sm" color="gray.600" mb={3}>PDF, images (scans), Word, CSV or text — one or many at a time</Text>
      <Button size="sm" onClick={() => inputRef.current && inputRef.current.click()} isDisabled={disabled}>Choose files…</Button>
      <input ref={inputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} onChange={e => { if (e.target.files.length) onFiles(Array.from(e.target.files)); e.target.value = ''; }} />
    </Box>
  );
}

function statusBadge(item) {
  if (item.uploading) return <Badge colorScheme="blue">uploading…</Badge>;
  if (item.error) return <Badge colorScheme="red">failed</Badge>;
  switch (item.status) {
    case 'approved': return <Badge colorScheme="green"><FiCheckCircle style={{ display: 'inline' }} /> approved</Badge>;
    case 'extracted': return <Badge colorScheme="teal">auto-read · approve</Badge>;
    case 'reviewed': return <Badge colorScheme="purple">edited</Badge>;
    case 'manual': return <Badge colorScheme="orange"><FiImage style={{ display: 'inline' }} /> scan · enter manually</Badge>;
    case 'pending_clarification': return <Badge colorScheme="orange"><FiClock style={{ display: 'inline' }} /> answers pending</Badge>;
    default: return <Badge colorScheme="yellow"><FiAlertCircle style={{ display: 'inline' }} /> needs review</Badge>;
  }
}

function Thumb({ item }) {
  const isImg = /^image\//.test(item.mime_type || '');
  if (item.uploading || !item.id) return <Flex w="72px" h="92px" bg="gray.100" borderRadius="md" align="center" justify="center"><Spinner size="sm" /></Flex>;
  if (isImg) return <Image src={fileUrl(item.id)} alt="" w="72px" h="92px" objectFit="cover" borderRadius="md" border="1px solid" borderColor="gray.200" />;
  if (/pdf/.test(item.mime_type || '')) return (
    <Box w="72px" h="92px" borderRadius="md" border="1px solid" borderColor="gray.200" overflow="hidden" bg="white" position="relative">
      <object data={`${fileUrl(item.id)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} type="application/pdf" width="220" height="280" style={{ transform: 'scale(0.33)', transformOrigin: '0 0', pointerEvents: 'none' }} aria-label="preview" />
    </Box>
  );
  return <Flex w="72px" h="92px" bg="gray.100" borderRadius="md" align="center" justify="center" color="gray.500"><FiFileText size={28} /></Flex>;
}

// ── review modal ──────────────────────────────────────────────────────────
/** Money input: shows two decimals (three for unit prices) when not being
 *  edited, lets the user type freely, commits a number (or null) on blur. */
function MoneyInput({ value, onChange, decimals = 2, ...rest }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const shown = editing ? text : (value == null || value === '' || isNaN(+value) ? '' : (+value).toFixed(decimals));
  return (
    <Input {...rest} type="text" inputMode="decimal" textAlign="right" value={shown}
      onFocus={(e) => { setEditing(true); setText(value == null ? '' : String(value)); e.target.select(); }}
      onChange={(e) => { const t = e.target.value.replace(/[^0-9.\-]/g, ''); setText(t); const n = parseFloat(t); onChange(t === '' || isNaN(n) ? null : n); }}
      onBlur={(e) => { setEditing(false); const n = parseFloat(text); onChange(text === '' || isNaN(n) ? null : +n.toFixed(decimals)); if (rest.onBlur) setTimeout(() => rest.onBlur(e), 0); }} />
  );
}

/** Per-line task allocation: "this task" by default, or search any task. */
function LineTaskPicker({ value, label, ownTaskId, ownTitle, onChange, isDisabled }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const timer = useRef(null);
  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try { const r = await api.get(`/invoice-intake/search?q=${encodeURIComponent(q.trim())}`); setResults(r.data || []); } catch (e) { setResults([]); }
    }, 400);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open]);
  const isOwn = !value || value === ownTaskId;
  if (!open) {
    return (
      <HStack spacing={1}>
        <Tooltip label={isOwn ? ownTitle : label}><Badge colorScheme={isOwn ? 'gray' : 'purple'} fontSize="8px" maxW="80px" isTruncated cursor={isDisabled ? 'default' : 'pointer'} onClick={() => !isDisabled && setOpen(true)}>{isOwn ? 'this task' : (label || 'other')}</Badge></Tooltip>
        {!isDisabled && !isOwn && <Button size="xs" variant="link" onClick={() => onChange(null)} title="back to this task">×</Button>}
      </HStack>
    );
  }
  return (
    <Box position="relative">
      <Input size="xs" autoFocus placeholder="Search company, project or task…" value={q} onChange={e => setQ(e.target.value)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {results.length > 0 && (
        <Box position="absolute" zIndex={20} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" shadow="md" mt={1} maxH="220px" overflowY="auto" w="320px">
          {results.map(r => (
            <Box key={r.task_id} px={2} py={1} fontSize="xs" cursor="pointer" _hover={{ bg: 'gray.100' }}
              onMouseDown={() => { onChange(r); setOpen(false); setQ(''); }}>
              <Text fontWeight="600" isTruncated>{r.task_title}</Text>
              <Text color="gray.500" isTruncated>{r.company_names ? `${r.company_names} › ` : ''}{r.project_name}</Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * ClarificationWizard — the questions the reader could not settle, one at a
 * time: paid or not (with the clues shown), how it was paid, when it is due,
 * anything that does not reconcile. "Answer later" parks the invoice in the
 * pending tray; finishing applies the answers to the form and records them.
 */
function ClarificationWizard({ questions, initial, onFinish, onLater, onClose }) {
  const [i, setI] = useState(0);
  const [a, setA] = useState(() => {
    const base = {};
    for (const q of questions) {
      if (q.kind === 'method') base[q.key] = { method: q.default || 'card', card_last4: q.card_last4 || '', paid_date: q.paid_date || '' };
      else base[q.key] = q.default ?? null;
    }
    return { ...base, ...(initial || {}) };
  });
  const visible = questions.filter(q => !q.when || Object.entries(q.when).every(([k, v]) => a[k] === v));
  const q = visible[Math.min(i, visible.length - 1)];
  const last = i >= visible.length - 1;
  if (!q) return null;
  const set = (v) => setA(p => ({ ...p, [q.key]: v }));
  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">A few questions about this invoice <Badge ml={2} colorScheme="gray">{Math.min(i + 1, visible.length)} of {visible.length}</Badge></ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontWeight="600" mb={2}>{q.text}</Text>
          {q.why && q.why.length > 0 && (
            <Box fontSize="xs" color="gray.600" bg="gray.50" p={2} borderRadius="md" mb={3}>
              <Text fontWeight="600" mb={0.5}>What I noticed</Text>
              {q.why.slice(0, 5).map((w, k) => <Text key={k}>• {w}</Text>)}
            </Box>
          )}
          {q.kind === 'choice' && (
            <VStack align="stretch" spacing={2}>
              {q.options.map(o => <Button key={o.value} size="sm" variant={a[q.key] === o.value ? 'solid' : 'outline'} colorScheme={a[q.key] === o.value ? 'teal' : 'gray'} justifyContent="flex-start" onClick={() => set(o.value)}>{o.label}</Button>)}
            </VStack>
          )}
          {q.kind === 'method' && (
            <VStack align="stretch" spacing={2}>
              <Select size="sm" value={a[q.key].method} onChange={e => set({ ...a[q.key], method: e.target.value })}>
                <option value="card">Company card</option><option value="paypal">PayPal</option><option value="direct_debit">Direct debit</option><option value="bacs">Bank transfer (BACS)</option><option value="cash">Cash</option><option value="personal">Personal card / expenses claim</option><option value="other">Other</option>
              </Select>
              {['card', 'personal'].includes(a[q.key].method) && <Input size="sm" placeholder="Card ending (last 4 digits)" maxLength={4} value={a[q.key].card_last4} onChange={e => set({ ...a[q.key], card_last4: e.target.value.replace(/\D/g, '').slice(0, 4) })} />}
              <HStack><Text fontSize="xs" minW="80px">Paid on</Text><Input size="sm" type="date" value={a[q.key].paid_date || ''} onChange={e => set({ ...a[q.key], paid_date: e.target.value })} /></HStack>
            </VStack>
          )}
          {q.kind === 'date' && <Input size="sm" type="date" value={a[q.key] || ''} onChange={e => set(e.target.value)} />}
          {q.kind === 'fuel' && <FuelStep value={a[q.key]} onChange={set} fuel={q.fuel} />}
          {q.kind === 'choice' && q.key === 'credit_note' && a[q.key] === 'yes' && <CreditStep value={a.credit_target || null} onChange={(v) => setA(p => ({ ...p, credit_target: v }))} hint={q.text} />}
          {q.kind === 'clarify' && (
            <VStack align="stretch" spacing={2}>
              <Text fontSize="xs" color="gray.600">Close this wizard to correct the figures or lines on the form; or tell me what the document actually says and carry on.</Text>
              <Input size="sm" placeholder="Note (optional) — e.g. discount not shown as a line" value={a[q.key] || ''} onChange={e => set(e.target.value)} />
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={() => onLater(a)}>Answer later</Button>
          <Box flex="1" />
          {i > 0 && <Button size="sm" variant="outline" onClick={() => setI(i - 1)}>Back</Button>}
          {!last && <Button size="sm" colorScheme="teal" isDisabled={q.kind === 'choice' && a[q.key] == null} onClick={() => setI(i + 1)}>Next</Button>}
          {last && <Button size="sm" colorScheme="green" isDisabled={q.kind === 'choice' && a[q.key] == null} onClick={() => onFinish(a)}>Done</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Fuel receipt → a journey (mileage record) or a fleet vehicle. Never a claim. */
function FuelStep({ value, onChange, fuel }) {
  const [opts, setOpts] = useState({ journeys: [], vehicles: [] });
  const [reg, setReg] = useState((fuel && fuel.registration) || '');
  useEffect(() => { api.get('/invoice-intake/fuel-options').then(r => setOpts(r.data || { journeys: [], vehicles: [] })).catch(() => {}); }, []);
  const v = value || { kind: 'none' };
  return (
    <VStack align="stretch" spacing={2} fontSize="sm">
      <HStack>
        <Button size="xs" variant={v.kind === 'journey' ? 'solid' : 'outline'} colorScheme="teal" onClick={() => onChange({ kind: 'journey' })}>A journey</Button>
        <Button size="xs" variant={v.kind === 'vehicle' ? 'solid' : 'outline'} colorScheme="teal" onClick={() => onChange({ kind: 'vehicle', registration: reg })}>A fleet vehicle</Button>
        <Button size="xs" variant={v.kind === 'none' ? 'solid' : 'outline'} onClick={() => onChange({ kind: 'none' })}>Neither</Button>
      </HStack>
      {v.kind === 'journey' && (
        <Select size="sm" value={v.mileage_claim_id || ''} onChange={e => onChange({ kind: 'journey', mileage_claim_id: e.target.value || null })}>
          <option value="">Choose a recorded journey…</option>
          {opts.journeys.map(j => <option key={j.id} value={j.id}>{String(j.claim_date).slice(0, 10)} · {j.start_address} → {j.end_address} · {j.miles} mi{j.project_name ? ` · ${j.project_name}` : ''}</option>)}
        </Select>
      )}
      {v.kind === 'vehicle' && (
        <VStack align="stretch" spacing={1}>
          <Select size="sm" value={v.vehicle_id || ''} onChange={e => onChange({ kind: 'vehicle', vehicle_id: e.target.value || null, registration: '' })}>
            <option value="">Choose a vehicle…</option>
            {opts.vehicles.map(x => <option key={x.id} value={x.id}>{x.registration}{x.make_model ? ` — ${x.make_model}` : ''}</option>)}
          </Select>
          <HStack><Text fontSize="xs" whiteSpace="nowrap">or add by registration</Text><Input size="sm" placeholder="AB12 CDE" value={reg} onChange={e => { setReg(e.target.value.toUpperCase()); onChange({ kind: 'vehicle', vehicle_id: null, registration: e.target.value.toUpperCase() }); }} /></HStack>
        </VStack>
      )}
      {fuel && (fuel.litres || fuel.pence_per_litre) && <Text fontSize="xs" color="gray.500">Read from the receipt: {fuel.litres ? `${fuel.litres} L` : ''}{fuel.pence_per_litre ? ` @ ${fuel.pence_per_litre} p/L` : ''}{fuel.fuel_type ? ` ${fuel.fuel_type}` : ''}.</Text>}
    </VStack>
  );
}

/** Credit note → which recorded invoice it credits (full or partial). */
function CreditStep({ value, onChange }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => { const t = setTimeout(() => api.get(`/invoice-intake/credit-candidates?q=${encodeURIComponent(q)}`).then(r => setRows(r.data || [])).catch(() => {}), 350); return () => clearTimeout(t); }, [q]);
  return (
    <VStack align="stretch" spacing={2} mt={3}>
      <Input size="sm" placeholder="Search recorded invoices by number or supplier…" value={q} onChange={e => setQ(e.target.value)} />
      <Box maxH="220px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
        {rows.map(r => (
          <HStack key={r.id} px={2} py={1} fontSize="xs" cursor="pointer" bg={value === r.id ? 'teal.50' : undefined} _hover={{ bg: 'gray.50' }} onClick={() => onChange(r.id)} justify="space-between">
            <Box><Text fontWeight="600">{r.supplier_name || '—'} · {r.invoice_number || '—'} · {String(r.issue_date).slice(0, 10)}</Text><Text color="gray.500">{r.project_name}{r.task_title ? ` › ${r.task_title}` : ''}</Text></Box>
            <Box textAlign="right"><Text>gross {(+r.total_gross).toFixed(2)}</Text>{+r.credited_so_far !== 0 && <Text color="purple.600">already credited {(+r.credited_so_far).toFixed(2)}</Text>}</Box>
          </HStack>
        ))}
        {rows.length === 0 && <Text p={2} fontSize="xs" color="gray.500">No recorded invoices match.</Text>}
      </Box>
    </VStack>
  );
}

/** Extra documents on this invoice: the receipt that goes with it, a delivery note, the PO… */
function Attachments({ item, approved }) {
  const [files, setFiles] = useState([]);
  const ref = useRef(null);
  const toast = useToast();
  const load = useCallback(() => api.get(`/invoice-intake/${item.id}/attachments`).then(r => setFiles(r.data || [])).catch(() => {}), [item.id]);
  useEffect(() => { load(); }, [load]);
  const add = async (list) => {
    const fd = new FormData(); Array.from(list || []).forEach(f => fd.append('files', f));
    try { const r = await api.post(`/invoice-intake/${item.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); const bad = (r.data || []).filter(x => x.error); if (bad.length) toast({ title: bad.map(b => `${b.original_name}: ${b.error}`).join('; '), status: 'error', duration: 5000 }); load(); }
    catch (e) { toast({ title: e?.response?.data?.error || 'Upload failed', status: 'error', duration: 3000 }); }
  };
  return (
    <HStack fontSize="xs" spacing={2} wrap="wrap">
      <Text color="gray.600" fontWeight="600">Attached:</Text>
      {files.map(f => <Button key={f.id} as="a" size="xs" variant="outline" href={`${process.env.REACT_APP_API_URL || '/api'}/files/${f.id}/download?token=${token()}`} target="_blank" rel="noreferrer">{f.original_name}</Button>)}
      {files.length === 0 && <Text color="gray.400">nothing else</Text>}
      <input ref={ref} type="file" multiple hidden accept=".pdf,image/*,.docx,.csv,.txt" onChange={e => { add(e.target.files); e.target.value = ''; }} />
      <Button size="xs" variant="link" leftIcon={<FiUpload />} onClick={() => ref.current && ref.current.click()}>Upload another document for this invoice</Button>
    </HStack>
  );
}

function ReviewModal({ item, onClose, onSaved, onApproved, targetPicker }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [showWorking, setShowWorking] = useState(false);
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get('/invoice-intake/users').then(r => setUsers(r.data || [])).catch(() => {}); }, []);
  const [saving, setSaving] = useState(false);
  const [supplierQ, setSupplierQ] = useState('');
  const dSupplierQ = useDebounce(supplierQ, 400);
  const [supplierRows, setSupplierRows] = useState([]);
  const [pickTarget, setPickTarget] = useState(false);
  const [target, setTarget] = useState({ project_id: item.project_id, project_name: item.project_name, task_id: item.task_id, task_title: item.task_title });

  useEffect(() => {
    api.get(`/invoice-intake/${item.id}`).then(r => {
      const d = r.data; setData(d);
      const ex = d.extraction || {};
      const base = { ...ex, ...(d.fields || {}) };
      if (!base.supplier_company_id && d.supplier_match && d.supplier_match.company) { base.supplier_company_id = d.supplier_match.company.id; base.supplier_company_name = d.supplier_match.company.name; base.supplier_match_reason = d.supplier_match.reason; }
      base.line_items = (base.line_items || []).map(li => ({ ...li }));
      if (!base.attributed_user_id) base.attributed_user_id = d.attributed_user_id || null;
      setF(base);
      // Ask the open questions once — not for approved invoices, not when already answered.
      const qs = (ex.questions || []).filter(q => q);
      if (d.status !== 'approved' && qs.length && !(d.answers && d.answers.paid !== undefined) && !(d.fields && d.fields._wizard_done)) setWizard({ questions: qs, initial: d.answers || null });
    }).catch(e => toast({ title: 'Could not load', description: e?.response?.data?.error, status: 'error' }));
  }, [item.id]); // eslint-disable-line

  useEffect(() => {
    if (!dSupplierQ.trim()) { setSupplierRows([]); return; }
    api.get(`/invoice-intake/suppliers?q=${encodeURIComponent(dSupplierQ.trim())}`).then(r => setSupplierRows(r.data || [])).catch(() => setSupplierRows([]));
  }, [dSupplierQ]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setF(p => { const items = p.line_items.map((it, j) => j === i ? { ...it, [k]: v } : it); return { ...p, line_items: items }; });
  // line total = qty × unit, from the LATEST state (blur fires after the value commit)
  const recalcLine = (i) => setF(p => ({ ...p, line_items: p.line_items.map((x, j) => j === i ? { ...x, amount: +(((parseFloat(x.quantity) || 1) * (parseFloat(x.unit_price) || 0)).toFixed(2)) } : x) }));
  const recalcFromItems = () => setF(p => {
    const net = p.line_items.reduce((s, it) => s + (parseFloat(it.amount) || (parseFloat(it.quantity) || 1) * (parseFloat(it.unit_price) || 0)), 0);
    const vat = p.line_items.reduce((s, it) => s + ((parseFloat(it.amount) || (parseFloat(it.quantity) || 1) * (parseFloat(it.unit_price) || 0)) * ((it.vat_rate != null ? parseFloat(it.vat_rate) : (p.vat_rate || 0)) / 100)), 0);
    return { ...p, net: +net.toFixed(2), vat: +vat.toFixed(2), gross: +(net + vat).toFixed(2) };
  });
  const sumsOk = f && f.net != null && f.vat != null && f.gross != null && Math.abs((+f.net) + (+f.vat) - (+f.gross)) <= 0.02;
  // The same arithmetic the server runs on approval, live as you edit.
  const checks = React.useMemo(() => {
    if (!f) return [];
    const close = (a, b, tol = 0.02) => a != null && b != null && Math.abs(a - b) <= tol;
    const n = f.net == null ? null : +f.net, v = f.vat == null ? null : +f.vat, g = f.gross == null ? null : +f.gross, rate = f.vat_rate == null ? null : +f.vat_rate;
    const money = (x) => x == null || isNaN(x) ? '—' : x.toFixed(2);
    const out = [];
    out.push({ key: 'sum', label: 'Net + VAT = total', ok: n != null && v != null && g != null ? close(n + v, g) : null, detail: `${money(n)} + ${money(v)} vs ${money(g)}` });
    if (rate != null && n != null && v != null) { const e = +(n * rate / 100).toFixed(2); out.push({ key: 'rate', label: `VAT is ${rate}% of net`, ok: close(e, v, 0.02 + n * 0.0005), detail: `${money(n)} × ${rate}% = ${money(e)} vs ${money(v)}` }); }
    else out.push({ key: 'rate', label: 'VAT rate', ok: f.reverse_charge || f.is_vat_invoice === false ? true : null, detail: f.reverse_charge ? 'reverse charge' : f.is_vat_invoice === false ? 'not a VAT invoice' : 'no rate' });
    const items = (f.line_items || []).filter(it => !it.summary);
    if (items.length) {
      const sum = +items.reduce((a, it) => a + (parseFloat(it.amount) || 0), 0).toFixed(2);
      const ship = +f.shipping || 0;
      const ok = n == null ? null : (close(sum, n, 0.03 + items.length * 0.005) || close(sum + ship, n, 0.03 + items.length * 0.005));
      out.push({ key: 'lines', label: `${items.length} line${items.length === 1 ? '' : 's'} add up to the net`, ok, detail: `${money(sum)}${ship && !close(sum, n) ? ` + shipping ${money(ship)}` : ''} vs net ${money(n)}` });
      const bad = items.filter(it => it.quantity != null && it.unit_price != null && it.amount != null && !close((+it.quantity) * (+it.unit_price), +it.amount, 0.02 + (+it.amount) * 0.002));
      out.push({ key: 'lines_math', label: 'Qty × unit = line total', ok: bad.length === 0, detail: bad.length ? `${bad.length} line${bad.length === 1 ? '' : 's'} do not multiply up` : 'all lines multiply up' });
    } else out.push({ key: 'lines', label: 'Line items', ok: null, detail: 'none — approving on totals alone' });
    out.push({ key: 'supplier', label: 'Supplier', ok: !!(f.supplier_company_id || f.supplier_name), detail: f.supplier_company_name || f.supplier_name || 'not identified' });
    out.push({ key: 'vatno', label: 'Supplier VAT number', ok: f.supplier_vat_number ? true : (f.is_vat_invoice === false ? true : null), detail: f.supplier_vat_number || (f.is_vat_invoice === false ? 'n/a' : 'missing') });
    out.push({ key: 'number', label: 'Invoice number', ok: !!f.invoice_number, detail: f.invoice_number || 'missing' });
    out.push({ key: 'date', label: 'Invoice date', ok: !!f.issue_date, detail: f.issue_date || 'missing' });
    return out;
  }, [f]);
  const failingChecks = checks.filter(c => c.ok === false && ['sum', 'rate', 'lines', 'lines_math'].includes(c.key));

  const save = async (approve) => {
    if (!f) return;
    if (approve && !target.project_id) { setPickTarget(true); toast({ title: 'Choose where this invoice goes first', status: 'warning', duration: 2500 }); return; }
    setSaving(true);
    try {
      const payload = { ...f }; delete payload.supplier_match_reason;
      if (approve) {
        let r;
      try {
        r = await api.post(`/invoice-intake/${item.id}/approve`, { fields: payload, project_id: target.project_id, task_id: target.task_id });
      } catch (e) {
        if (e?.response?.status === 422 && e.response.data?.checks) {
          const fails = e.response.data.checks.filter(c => c.ok === false).map(c => `• ${c.label}: ${c.detail}`).join('\n');
          if (!window.confirm(`The figures do not reconcile:\n\n${fails}\n\nRecord the invoice anyway, exactly as entered? (This is noted on the invoice.)`)) { setSaving(false); return; }
          r = await api.post(`/invoice-intake/${item.id}/approve`, { fields: { ...payload, override_checks: true }, project_id: target.project_id, task_id: target.task_id });
        } else throw e;
      }
      if (r.data?.supplier_created) toast({ title: `Added ${f.supplier_name} as a supplier`, description: 'Assigned to this project and task.', status: 'info', duration: 4000 });
        toast({ title: 'Invoice recorded', description: `${payload.supplier_name || 'Supplier'} · ${fmtMoney(payload.gross)} ${payload.currency || 'GBP'}`, status: 'success', duration: 2500 });
        onApproved(r.data);
      } else {
        const r = await api.put(`/invoice-intake/${item.id}`, { fields: payload, project_id: target.project_id, task_id: target.task_id });
        onSaved(r.data); toast({ title: 'Saved', status: 'success', duration: 1200 });
      }
    } catch (e) { toast({ title: approve ? 'Approve failed' : 'Save failed', description: e?.response?.data?.error || e.message, status: 'error', duration: 4000 }); }
    finally { setSaving(false); }
  };
  const applyAnswers = async (a) => {
    setWizard(null);
    setF(p => {
      const n = { ...p, _wizard_done: true };
      if (a.paid === 'yes') {
        n.paid = true; n.status = 'recv_paid_in_full';
        const m = a.paid_how || {};
        n.payment_method = m.method || 'card'; n.card_last4 = m.card_last4 || p.card_last4 || null; n.date_paid = m.paid_date || p.issue_date || null;
      } else if (a.paid === 'no') { n.paid = false; n.status = p.status === 'recv_paid_in_full' ? 'recv_pay_within_terms' : (p.status || 'recv_pay_within_terms'); n.payment_method = null; n.card_last4 = null; n.date_paid = null; if (a.due_date) n.due_date = a.due_date; }
      if (a.line_vat === 'yes' && n.vat_rate != null) n.line_items = (n.line_items || []).map(li => ({ ...li, vat_rate: li.vat_rate == null ? n.vat_rate : li.vat_rate }));
      for (const k of Object.keys(a)) if (k.startsWith('check_') && a[k]) n.internal_notes = `${p.internal_notes ? p.internal_notes + '; ' : ''}${a[k]}`;
      if (a.credit_note === 'yes') { n.document_type = 'credit_note'; n.credit_for_invoice_id = a.credit_target || null; }
      else if (a.credit_note === 'standalone') { n.document_type = 'credit_note'; n.credit_for_invoice_id = null; }
      else if (a.credit_note === 'no') { n.document_type = 'invoice'; for (const k of ['net', 'vat', 'gross']) if (n[k] != null && n[k] < 0) n[k] = Math.abs(n[k]); n.line_items = (n.line_items || []).map(li => ({ ...li, amount: Math.abs(+li.amount || 0), unit_price: Math.abs(+li.unit_price || 0) })); }
      if (a.duplicate === 'credit' && p.duplicate_of && p.duplicate_of.invoice_id) { n.document_type = 'credit_note'; n.credit_for_invoice_id = p.duplicate_of.invoice_id; for (const k of ['net', 'vat', 'gross']) if (n[k] != null && n[k] > 0) n[k] = -n[k]; }
      if (a.duplicate === 'no' && p.duplicate_of && p.duplicate_of.invoice_id) n.duplicate_of_invoice_id = null;
      if (a.fuel) {
        n.document_type = n.document_type === 'credit_note' ? n.document_type : 'fuel_receipt';
        n.mileage_claim_id = a.fuel.kind === 'journey' ? a.fuel.mileage_claim_id || null : null;
        n.vehicle_id = a.fuel.kind === 'vehicle' ? a.fuel.vehicle_id || null : null;
        n.new_vehicle_registration = a.fuel.kind === 'vehicle' && !a.fuel.vehicle_id ? a.fuel.registration || null : null;
      }
      return n;
    });
    if (a.duplicate === 'duplicate') {
      // the user confirmed a duplicate: discard this upload
      try { await api.delete(`/invoice-intake/${item.id}`); toast({ title: 'Duplicate discarded', status: 'info', duration: 2500 }); onApproved({ id: item.id, status: 'discarded' }); return; } catch (e) { /* fall through */ }
    }
    try { await api.put(`/invoice-intake/${item.id}`, { answers: a }); } catch (e) { /* non-fatal */ }
  };
  const answerLater = async (a) => {
    setWizard(null);
    try { const r = await api.put(`/invoice-intake/${item.id}`, { answers: a, answer_later: true }); onSaved(r.data); toast({ title: 'Parked in the pending tray', description: 'You can finish the questions any time.', status: 'info', duration: 2500 }); onClose(); }
    catch (e) { toast({ title: 'Could not park', description: e?.response?.data?.error, status: 'error', duration: 3000 }); }
  };
  const reopen = async () => {
    if (!window.confirm('Reopen this approved invoice for editing? Re-approving will update the recorded invoice (the previous version is kept in its history).')) return;
    setSaving(true);
    try { const r = await api.post(`/invoice-intake/${item.id}/reopen`); onSaved(r.data); }
    catch (e) { toast({ title: 'Could not reopen', description: e?.response?.data?.error || '', status: 'error', duration: 3000 }); }
    setSaving(false);
  };
  const reextract = async () => {
    setSaving(true);
    try { const r = await api.post(`/invoice-intake/${item.id}/reextract`); onSaved(r.data); setF({ ...(r.data.extraction || {}), line_items: (r.data.extraction?.line_items || []).map(x => ({ ...x })) }); toast({ title: 'Re-read from the document', status: 'info', duration: 1500 }); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.error, status: 'error' }); }
    finally { setSaving(false); }
  };

  const approved = item.status === 'approved';
  const isImg = /^image\//.test(item.mime_type || '');
  const ex = data?.extraction || {};
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" overflow="hidden">
        {/* header: the way out is the first thing on the bar */}
        <Flex py={2} px={3} borderBottom="1px solid" borderColor="gray.200" align="center" gap={3} minW={0} wrap="wrap">
          <Button size="sm" variant="outline" leftIcon={<FiArrowLeft />} onClick={onClose} flexShrink={0}>Back to invoices</Button>
          <HStack minW={0} flex="1 1 300px"><FiFileText /><Text fontSize="md" isTruncated minW={0}>{item.original_name}</Text>{statusBadge(item)}</HStack>
          <HStack spacing={2} flexShrink={0}>
            {approved && <Button size="sm" variant="outline" colorScheme="orange" leftIcon={<FiEdit2 />} onClick={reopen} isLoading={saving}>Reopen &amp; edit</Button>}
            {!approved && <Button size="sm" variant="ghost" leftIcon={<FiRefreshCw />} onClick={reextract} isLoading={saving}>Auto-populate again</Button>}
            {!approved && <Button size="sm" variant="outline" onClick={() => save(false)} isLoading={saving}>Save</Button>}
            {!approved && <Button size="sm" colorScheme="green" leftIcon={<FiCheckCircle />} onClick={() => save(true)} isLoading={saving}>Approve &amp; record invoice</Button>}
          </HStack>
        </Flex>
        <Box p={0}>
          {wizard && <ClarificationWizard questions={wizard.questions} initial={wizard.initial} onFinish={applyAnswers} onLater={answerLater} onClose={() => { setWizard(null); setF(p => ({ ...p, _wizard_done: true })); }} />}
          <Flex h="calc(100vh - 150px)" minH="520px" direction={{ base: 'column', lg: 'row' }} overflow="hidden" minW={0}>
            {/* left: the document — exactly half, own scrollbar, opens at 84% */}
            <Box flex="0 0 50%" w="50%" minW={0} bg="gray.100" minH="300px" overflow="auto">
              {isImg
                ? <Flex minH="100%" align="flex-start" justify="center" p={4}><Image src={fileUrl(item.id)} alt="" maxW="100%" objectFit="contain" /></Flex>
                : <object data={`${fileUrl(item.id)}#zoom=84&view=FitH`} type={item.mime_type || 'application/pdf'} width="100%" height="100%" aria-label="document">
                    <Flex h="100%" align="center" justify="center"><Button as="a" href={fileUrl(item.id)} target="_blank" rel="noreferrer">Open document</Button></Flex>
                  </object>}
            </Box>
            {/* right: interpreted information */}
            {/* FormControls default to width:100%, so several in an HStack overflow
                the pane (and pushed the header buttons off-screen) — make every
                control in this pane share the row instead. */}
            <Box flex="0 0 50%" w="50%" minW={0} overflowY="auto" overflowX="auto" p={5} borderLeft="1px solid" borderColor="gray.200"
              sx={{ '.chakra-form-control': { flex: '1 1 0', minWidth: 0 } }}>
              {!f ? <Flex justify="center" py={10}><Spinner /></Flex> : (
                <VStack align="stretch" spacing={4}>
                  {item.status === 'manual' && (
                    <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3} fontSize="sm">
                      <b>Scanned image</b> — no readable text was found, so nothing was auto-populated. Enter the details from the image on the left. (Automatic reading of scans is a planned addition.)
                    </Box>
                  )}
                  {(() => {
                    // Drop warnings the reviewer has already resolved (e.g. supplier now set).
                    const live = (ex.warnings || []).filter(w => !(/supplier not identified/i.test(w) && (f.supplier_name || f.supplier_company_id))
                      && !(/no invoice date/i.test(w) && f.issue_date) && !(/no invoice number/i.test(w) && f.invoice_number));
                    return live.length > 0 && item.status !== 'manual' ? (
                      <Box bg="yellow.50" border="1px solid" borderColor="yellow.200" borderRadius="md" p={3} fontSize="xs">
                        {live.map((w, i) => <Text key={i}>• {w}</Text>)}
                      </Box>
                    ) : null;
                  })()}

                  {(f.document_type && f.document_type !== 'invoice') && (
                    <HStack><Badge colorScheme={f.document_type === 'credit_note' ? 'purple' : f.document_type === 'fuel_receipt' ? 'orange' : 'gray'}>{f.document_type.replace('_', ' ')}</Badge>
                      {f.document_type === 'credit_note' && <Text fontSize="xs" color="gray.600">recorded as negative amounts{f.credit_for_invoice_id ? ' against the chosen invoice' : ' (no original invoice recorded)'}</Text>}
                      {f.document_type === 'fuel_receipt' && <Text fontSize="xs" color="gray.600">{f.mileage_claim_id ? 'related to a journey' : f.vehicle_id || f.new_vehicle_registration ? `related to vehicle ${f.new_vehicle_registration || ''}` : 'not yet related to a journey or vehicle'} — not a mileage claim</Text>}
                      {!approved && <Button size="xs" variant="link" onClick={() => set('document_type', 'invoice')}>treat as an ordinary invoice</Button>}
                    </HStack>
                  )}
                  <Attachments item={item} approved={approved} />
                  {/* target */}
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase">Goes to</Text>
                      {!approved && <Button size="xs" variant="link" onClick={() => setPickTarget(v => !v)}>{target.project_id ? 'Change' : 'Choose project & task'}</Button>}
                    </HStack>
                    {target.project_id
                      ? <Text fontSize="sm"><FiFolder style={{ display: 'inline' }} /> <b>{target.project_name}</b>{target.task_title ? ` › ${target.task_title}` : ''}</Text>
                      : <Text fontSize="sm" color="red.600">Not yet assigned to a project.</Text>}
                    {pickTarget && !approved && <Box mt={2} p={3} bg="gray.50" borderRadius="md">{targetPicker((t) => { setTarget({ project_id: t.project_id, project_name: t.project_name, task_id: t.task_id, task_title: t.task_title }); setPickTarget(false); })}</Box>}
                  </Box>
                  <Divider />
                  {/* supplier */}
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Supplier</Text>
                    <HStack align="flex-start">
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>Name on the invoice</FormLabel>
                        <Input size="sm" value={f.supplier_name || ''} onChange={e => set('supplier_name', e.target.value)} isDisabled={approved} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>Supplier VAT number</FormLabel>
                        <Input size="sm" value={f.supplier_vat_number || ''} onChange={e => set('supplier_vat_number', e.target.value)} isDisabled={approved} placeholder="e.g. GB123456789" />
                      </FormControl>
                    </HStack>
                  {f.supplier_legal_name && f.supplier_legal_name !== f.supplier_name && (
                    <Text fontSize="xs" color="gray.500" mt={1}>Trading name of <b>{f.supplier_legal_name}</b> (from the invoice footer).</Text>
                  )}
                    <Box mt={2}>
                      {f.supplier_company_id
                        ? <HStack><Tag colorScheme="teal" size="sm">Linked to company: {f.supplier_company_name || 'selected'}{f.supplier_match_reason ? ` (matched by ${f.supplier_match_reason})` : ''}</Tag>
                            {!approved && <Button size="xs" variant="ghost" onClick={() => { set('supplier_company_id', null); set('supplier_company_name', null); }}>unlink</Button>}</HStack>
                        : (<Box>
                            <HStack><Input size="sm" placeholder="Link to an existing company (search by name or VAT number)…" value={supplierQ} onChange={e => setSupplierQ(e.target.value)} isDisabled={approved} />
                              <Tooltip label={f.create_supplier === false ? 'A new supplier company will NOT be created' : 'A new supplier company will be created from the name above if no link is chosen'}>
                                <HStack><Text fontSize="xs" whiteSpace="nowrap">Add as supplier &amp; assign to this project/task</Text><Switch size="sm" isChecked={f.create_supplier !== false} onChange={e => set('create_supplier', e.target.checked)} isDisabled={approved} /></HStack>
                              </Tooltip></HStack>
                            {supplierRows.length > 0 && <VStack align="stretch" spacing={0} mt={1} border="1px solid" borderColor="gray.200" borderRadius="md" maxH="160px" overflowY="auto">
                              {supplierRows.map(c => <Box key={c.id} as="button" textAlign="left" px={2} py={1} fontSize="sm" _hover={{ bg: 'gray.50' }}
                                onClick={() => { set('supplier_company_id', c.id); set('supplier_company_name', c.name); if (!f.supplier_vat_number && c.vat_number) set('supplier_vat_number', c.vat_number); setSupplierQ(''); setSupplierRows([]); }}>
                                {c.name}{c.vat_number ? <Text as="span" fontSize="xs" color="gray.500"> · {c.vat_number}</Text> : null}</Box>)}
                            </VStack>}
                          </Box>)}
                    </Box>
                  </Box>
                  <Divider />
                  {/* header fields */}
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Invoice</Text>
                    <HStack align="flex-start">
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Invoice number</FormLabel>
                        <Input size="sm" value={f.invoice_number || ''} onChange={e => set('invoice_number', e.target.value)} isDisabled={approved} placeholder={f.order_reference ? `order ref ${f.order_reference}` : ''} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Order / reference</FormLabel>
                        <Input size="sm" value={f.order_reference || ''} onChange={e => set('order_reference', e.target.value)} isDisabled={approved} /></FormControl>
                    </HStack>
                    <HStack align="flex-start" mt={2}>
                      <FormControl isRequired><FormLabel fontSize="xs" mb={0.5}>Invoice date</FormLabel>
                        <Input size="sm" type="date" value={f.issue_date || ''} onChange={e => set('issue_date', e.target.value)} isDisabled={approved} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Due date</FormLabel>
                        <Input size="sm" type="date" value={f.due_date || ''} onChange={e => set('due_date', e.target.value)} isDisabled={approved} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Currency</FormLabel>
                        <Select size="sm" value={f.currency || 'GBP'} onChange={e => set('currency', e.target.value)} isDisabled={approved}><option>GBP</option><option>EUR</option><option>USD</option></Select></FormControl>
                    </HStack>
                    <HStack align="flex-start" mt={2}>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Net</FormLabel>
                        <MoneyInput size="sm" value={f.net} onChange={v => set('net', v)} isDisabled={approved} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>VAT</FormLabel>
                        <MoneyInput size="sm" value={f.vat} onChange={v => set('vat', v)} isDisabled={approved} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>VAT rate %</FormLabel>
                        <Input size="sm" type="number" step="0.5" value={f.vat_rate ?? ''} onChange={e => set('vat_rate', e.target.value === '' ? null : +e.target.value)} isDisabled={approved} /></FormControl>
                      <FormControl isRequired><FormLabel fontSize="xs" mb={0.5}>Total (gross)</FormLabel>
                        <MoneyInput size="sm" fontWeight="700" value={f.gross} onChange={v => set('gross', v)} isDisabled={approved} /></FormControl>
                    </HStack>
                    <HStack mt={2} spacing={4} fontSize="xs">
                      {f.net != null && f.vat != null && f.gross != null && (sumsOk ? <Text color="green.600"><FiCheckCircle style={{ display: 'inline' }} /> net + VAT = total</Text> : <Text color="red.600"><FiAlertCircle style={{ display: 'inline' }} /> net + VAT ≠ total — check the figures</Text>)}
                      <HStack><Switch size="sm" isChecked={!!f.paid} onChange={e => set('paid', e.target.checked)} isDisabled={approved} /><Text>Already paid</Text></HStack>
                      <HStack><Switch size="sm" isChecked={f.is_vat_invoice !== false} onChange={e => set('is_vat_invoice', e.target.checked)} isDisabled={approved} /><Text>VAT invoice</Text></HStack>
                      {f.reverse_charge && <Badge colorScheme="purple">reverse charge</Badge>}
                    </HStack>
                    <FormControl mt={2}><FormLabel fontSize="xs" mb={0.5}>Status when recorded</FormLabel>
                      <Select size="sm" maxW="280px" value={f.status || (f.paid ? 'recv_paid_in_full' : 'recv_pay_within_terms')} onChange={e => set('status', e.target.value)} isDisabled={approved}>
                        <option value="recv_pay_within_terms">To pay within terms</option>
                        <option value="recv_paid_in_full">Paid in full</option>
                        <option value="recv_assign_po">Assign PO</option>
                        <option value="recv_receipt">Receipt (card / already settled)</option>
                        <option value="recv_disputed">Disputed</option>
                      </Select></FormControl>
                  <HStack align="flex-start" mt={2}>
                    <FormControl><FormLabel fontSize="xs" mb={0.5}>Attributed to</FormLabel>
                      <Select size="sm" value={f.attributed_user_id || ''} onChange={e => set('attributed_user_id', e.target.value || null)} isDisabled={approved}>
                        <option value="">— me —</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl><FormLabel fontSize="xs" mb={0.5}>How paid</FormLabel>
                      <Select size="sm" value={f.payment_method || ''} onChange={e => set('payment_method', e.target.value || null)} isDisabled={approved}>
                        <option value="">—</option><option value="card">Company card</option><option value="paypal">PayPal</option><option value="direct_debit">Direct debit</option><option value="bacs">Bank transfer</option><option value="cash">Cash</option><option value="personal">Personal card / expenses</option><option value="other">Other</option>
                      </Select>
                    </FormControl>
                    <FormControl maxW="120px"><FormLabel fontSize="xs" mb={0.5}>Card ending</FormLabel>
                      <Input size="sm" maxLength={4} value={f.card_last4 || ''} onChange={e => set('card_last4', e.target.value.replace(/\D/g, '').slice(0, 4))} isDisabled={approved} />
                    </FormControl>
                  </HStack>
                  {item.uploaded_by_name && <Text fontSize="10px" color="gray.500">Uploaded by {item.uploaded_by_name}{item.source === 'portal' ? ' via an upload link' : ''}.</Text>}
                  {(ex.payment && ex.payment.reasons && ex.payment.reasons.length > 0) && !approved && (
                    <HStack fontSize="10px" color="gray.500" spacing={2}><Text>Payment clues: {ex.payment.reasons.slice(0, 3).join('; ')}</Text>
                      {(ex.questions || []).length > 0 && <Button size="xs" variant="link" onClick={() => setWizard({ questions: ex.questions, initial: item.answers || null })}>ask me again</Button>}</HStack>
                  )}
                  </Box>
                  <Divider />
                  {/* line items */}
                  {/* ── Checks: what a bookkeeper verifies before posting ── */}
                  <Box border="1px solid" borderColor={failingChecks.length ? 'red.200' : 'gray.200'} bg={failingChecks.length ? 'red.50' : 'gray.50'} borderRadius="md" p={3}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.600">Checks against the document</Text>
                      <HStack>
                        <Badge colorScheme={failingChecks.length ? 'red' : checks.some(c => c.ok === null) ? 'yellow' : 'green'}>
                          {failingChecks.length ? `${failingChecks.length} failing` : checks.some(c => c.ok === null) ? 'incomplete' : `all ${checks.length} pass`}
                        </Badge>
                        {!failingChecks.length && !checks.some(c => c.ok === null) && <Button size="xs" variant="link" onClick={() => setShowWorking(v => !v)}>{showWorking ? 'hide working' : 'show working'}</Button>}
                      </HStack>
                    </HStack>
                    <VStack align="stretch" spacing={0.5} fontSize="xs" display={(failingChecks.length || checks.some(c => c.ok === null) || showWorking) ? 'flex' : 'none'}>
                      {checks.map(c => (
                        <HStack key={c.key} spacing={2}>
                          <Box w="14px" color={c.ok === true ? 'green.600' : c.ok === false ? 'red.600' : 'gray.400'}>{c.ok === true ? <FiCheckCircle /> : c.ok === false ? <FiAlertCircle /> : '•'}</Box>
                          <Text minW="190px" fontWeight="600">{c.label}</Text>
                          <Text color="gray.600" isTruncated>{c.detail}</Text>
                        </HStack>
                      ))}
                    </VStack>
                    {failingChecks.length > 0 && !approved && <Text fontSize="xs" color="red.700" mt={1}>Correct the figures or lines above; approving will ask you to confirm recording it as entered.</Text>}
                  </Box>
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase">Line items (optional — totals above are what count)</Text>
                      {!approved && <HStack><Button size="xs" variant="ghost" onClick={recalcFromItems}>Totals from lines</Button><Button size="xs" leftIcon={<FiPlus />} onClick={() => setF(p => ({ ...p, line_items: [...p.line_items, { description: '', quantity: 1, unit_price: 0, vat_rate: p.vat_rate ?? 20, amount: 0 }] }))}>Add line</Button></HStack>}
                    </HStack>
                    {(() => {
                      const lineVat = (it) => { const amt = parseFloat(it.amount) || ((parseFloat(it.quantity) || 1) * (parseFloat(it.unit_price) || 0)); const r = it.vat_rate == null || it.vat_rate === '' ? (f.vat_rate ?? 0) : +it.vat_rate; return +(amt * r / 100).toFixed(2); };
                      const sumNet = +f.line_items.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0).toFixed(2);
                      const sumVat = +f.line_items.reduce((acc, it) => acc + lineVat(it), 0).toFixed(2);
                      const treatment = f.reverse_charge ? 'reverse charge' : f.is_vat_invoice === false || (+f.vat || 0) === 0 ? 'no VAT' : (f.line_items.some(it => (it.vat_rate ?? f.vat_rate) === 0) && f.line_items.some(it => (it.vat_rate ?? f.vat_rate) > 0)) ? 'mixed rates' : 'standard';
                      const claimable = (treatment === 'reverse charge' || treatment === 'no VAT' || !f.supplier_vat_number) ? 0 : (f.vat != null && Math.abs(+f.vat - sumVat) <= 0.05 ? +f.vat : sumVat);
                      const money = (v) => (+v || 0).toFixed(2);
                      return (
                        <>
                          <Box overflowX="auto">
                            <Table size="sm" variant="simple" minW="820px">
                              <Thead><Tr>
                                <Th>Description</Th><Th isNumeric w="64px">Qty</Th><Th isNumeric w="96px">Unit (net)</Th><Th isNumeric w="70px">VAT %</Th>
                                <Th isNumeric w="84px">VAT £</Th><Th isNumeric w="96px">Line (net)</Th><Th w="120px">For task</Th><Th w="32px"></Th>
                              </Tr></Thead>
                              <Tbody>
                                {f.line_items.map((it, i) => (
                                  <Tr key={i} bg={it.is_shipping ? 'orange.50' : undefined}>
                                    <Td p={1}>
                                      <HStack spacing={1}>
                                        {it.is_shipping && <Badge colorScheme="orange" fontSize="8px" flexShrink={0}>Delivery</Badge>}
                                        <Input size="xs" value={it.description || ''} onChange={e => setItem(i, 'description', e.target.value)} isDisabled={approved} />
                                      </HStack>
                                    </Td>
                                    <Td p={1}><Input size="xs" type="number" textAlign="right" value={it.quantity ?? 1} onChange={e => setItem(i, 'quantity', +e.target.value)} onBlur={() => recalcLine(i)} isDisabled={approved} /></Td>
                                    <Td p={1}><MoneyInput size="xs" decimals={3} value={it.unit_price} onChange={v => setItem(i, 'unit_price', v)} onBlur={() => recalcLine(i)} isDisabled={approved} /></Td>
                                    <Td p={1}><Input size="xs" type="number" step="0.5" textAlign="right" placeholder={String(f.vat_rate ?? 0)} value={it.vat_rate ?? ''} onChange={e => setItem(i, 'vat_rate', e.target.value === '' ? null : +e.target.value)} isDisabled={approved} /></Td>
                                    <Td p={1} isNumeric><Text fontSize="xs" color={lineVat(it) ? 'gray.800' : 'gray.400'}>{money(lineVat(it))}</Text></Td>
                                    <Td p={1}><MoneyInput size="xs" value={it.amount} onChange={v => setItem(i, 'amount', v)} isDisabled={approved} /></Td>
                                    <Td p={1}>
                                      {it.is_shipping
                                        ? <Text fontSize="10px" color="gray.500">{f.delivery_sharing === 'keep' ? 'kept by this task' : 'shared pro-rata'}</Text>
                                        : <LineTaskPicker value={it.task_id || null} label={it.task_title || null} ownTaskId={target.task_id} ownTitle={target.task_title}
                                            onChange={(t) => setF(p => ({ ...p, line_items: p.line_items.map((x, j) => j === i ? { ...x, task_id: t ? t.task_id : null, task_title: t ? t.task_title : null, task_project: t ? t.project_name : null } : x) }))}
                                            isDisabled={approved} />}
                                    </Td>
                                    <Td p={1}>{!approved && <IconButton size="xs" variant="ghost" icon={<FiX />} aria-label="remove" onClick={() => setF(p => ({ ...p, line_items: p.line_items.filter((_, j) => j !== i) }))} />}</Td>
                                  </Tr>
                                ))}
                                {f.line_items.length === 0 && <Tr><Td colSpan={8}><Text fontSize="xs" color="gray.500">No lines — a single summary line will be recorded from the totals.</Text></Td></Tr>}
                                {f.line_items.length > 0 && (
                                  <Tr fontWeight="700"><Td p={1} colSpan={4} textAlign="right" fontSize="xs">Lines total</Td><Td p={1} isNumeric fontSize="xs">{money(sumVat)}</Td><Td p={1} isNumeric fontSize="xs">{money(sumNet)}</Td><Td p={1} colSpan={2}></Td></Tr>
                                )}
                              </Tbody>
                            </Table>
                          </Box>
                          {f.line_items.some(it => it.task_id && it.task_id !== target.task_id) && f.line_items.some(it => it.is_shipping) && (
                            <HStack mt={2} p={2} bg="purple.50" borderRadius="md" fontSize="xs" spacing={3}>
                              <Text fontWeight="600">Lines go to more than one task — delivery costs:</Text>
                              <Button size="xs" variant={f.delivery_sharing !== 'keep' ? 'solid' : 'outline'} colorScheme="purple" onClick={() => set('delivery_sharing', 'share')} isDisabled={approved}>Share pro-rata</Button>
                              <Button size="xs" variant={f.delivery_sharing === 'keep' ? 'solid' : 'outline'} colorScheme="purple" onClick={() => set('delivery_sharing', 'keep')} isDisabled={approved}>Keep on this task</Button>
                            </HStack>
                          )}
                          {!approved && <HStack mt={1} spacing={3}>
                            <Button size="xs" variant="link" onClick={() => setF(p => ({ ...p, line_items: [...p.line_items, { description: 'Delivery', quantity: 1, unit_price: 0, amount: 0, vat_rate: p.vat_rate ?? 0, is_shipping: true }] }))}>+ Delivery line</Button>
                            <Text fontSize="10px" color="gray.500">VAT is worked out per line at that line's rate — 0% lines stay at 0%; delivery carries its own rate.</Text>
                          </HStack>}
                          {/* VAT metadata recorded with the invoice */}
                          <Box mt={3} p={2} bg="gray.50" borderRadius="md" fontSize="xs">
                            <HStack spacing={6} wrap="wrap">
                              <Text><b>VAT treatment:</b> {treatment}</Text>
                              <Text><b>VAT on lines:</b> {money(sumVat)}{f.vat != null && Math.abs(+f.vat - sumVat) > 0.05 ? <Text as="span" color="red.600"> (invoice says {money(f.vat)})</Text> : null}</Text>
                              <Text><b>Claimable VAT:</b> <b>{money(claimable)}</b>{claimable === 0 && (+f.vat || 0) > 0 ? <Text as="span" color="orange.600"> — {treatment === 'reverse charge' ? 'reverse charge: accounted both ways, net nil' : !f.supplier_vat_number ? 'no supplier VAT number on the invoice' : 'not a VAT invoice'}</Text> : null}</Text>
                            </HStack>
                          </Box>
                        </>
                      );
                    })()}
                    {f.line_items.some(li => li.amount_includes_vat) && <Text fontSize="xs" color="orange.600" mt={1}>Line amounts on this document include VAT.</Text>}
                  </Box>
                  {data && <Text fontSize="10px" color="gray.400">Read by: {data.method || '—'} · confidence {Math.round((data.confidence || 0) * 100)}%</Text>}
                </VStack>
              )}
            </Box>
          </Flex>
        </Box>
        <Flex borderTop="1px solid" borderColor="gray.200" py={2} px={3} gap={2} align="center">
          <Button size="sm" variant="ghost" leftIcon={<FiArrowLeft />} onClick={onClose}>{approved ? 'Back to invoices' : 'Back without approving'}</Button>
          <Box flex="1" />
          {approved && <Button size="sm" variant="outline" colorScheme="orange" leftIcon={<FiEdit2 />} onClick={reopen} isLoading={saving}>Reopen &amp; edit</Button>}
          {!approved && <Button size="sm" variant="outline" onClick={() => save(false)} isLoading={saving}>Save for later</Button>}
          {!approved && <Button size="sm" colorScheme="green" leftIcon={<FiCheckCircle />} onClick={() => save(true)} isLoading={saving}>Approve &amp; record invoice</Button>}
        </Flex>
    </Box>
  );
}

// ── dashboard ─────────────────────────────────────────────────────────────
export default function InvoiceIntakeDashboard({ onBack }) {
  const toast = useToast();
  const { user } = useAuth();
  // Every view has its own URL:
  //   /accounts/invoices                 tiles
  //   /accounts/invoices/single?task=…   one project & task (task preset from the grid)
  //   /accounts/invoices/multi           drop first, choose targets after
  //   /accounts/invoices/pending         the tray
  //   /accounts/invoices/portal          my receipt upload link + organisation setting
  //   /accounts/invoices/review/<id>     one invoice, on its own page
  const { navigate, pathname, search } = useNav();
  const url = new URL(pathname + search, window.location.origin);
  const seg = url.pathname.replace(/^\/accounts\/invoices\/?/, '').split('/').filter(Boolean);
  const mode = seg[0] === 'review' ? 'review' : (['single', 'multi', 'pending', 'portal'].includes(seg[0]) ? seg[0] : null);
  const reviewId = mode === 'review' ? seg[1] : null;
  const presetTaskId = url.searchParams.get('task');
  const returnTo = url.searchParams.get('from') || '/accounts/invoices';
  const setMode = (m) => navigate(m ? `/accounts/invoices/${m}` : '/accounts/invoices');
  const [target, setTarget] = useState(null);        // single mode target
  const [recent, setRecent] = useState([]);
  const [items, setItems] = useState([]);
  const [review, setReviewState] = useState(null);
  const setReview = (it) => { if (it) navigate(`/accounts/invoices/review/${it.id}?from=${encodeURIComponent(window.location.pathname + window.location.search)}`); else navigate(returnTo); };
  const [assigning, setAssigning] = useState(null);  // multi mode: item id picking a target
  const [taskInvoices, setTaskInvoices] = useState(null); // invoices already recorded on the current target task
  const [pending, setPending] = useState([]);              // the tray: no task yet, or answers pending
  const [portal, setPortal] = useState(null);              // my receipt upload link
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalSettings, setPortalSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const loadPending = useCallback(() => api.get('/invoice-intake/pending').then(r => setPending(r.data || [])).catch(() => {}), []);
  useEffect(() => { loadPending(); api.get('/invoice-intake/users').then(r => setUsers(r.data || [])).catch(() => {}); }, [loadPending]);
  useEffect(() => { if (mode === 'portal' && !portal) openPortal(); }, [mode]); // eslint-disable-line
  const openPortal = async () => {
    setPortalOpen(true);
    if (mode !== 'portal') navigate('/accounts/invoices/portal');
    try { const r = await api.get('/invoice-intake/portals/mine'); setPortal(r.data); } catch (e) { toast({ title: 'Could not load your link', status: 'error', duration: 2500 }); }
    try { const r = await api.get('/invoice-intake/portals/settings'); setPortalSettings(r.data); } catch (e) { /* non-admin */ }
  };

  const loadRecent = useCallback(() => api.get('/invoice-intake/recent-targets').then(r => setRecent(r.data || [])).catch(() => {}), []);
  const loadItems = useCallback(() => api.get('/invoice-intake').then(r => setItems(r.data || [])).catch(() => {}), []);
  useEffect(() => { loadRecent(); loadItems(); }, [loadRecent, loadItems]);
  // Opened from a task row in the grid: resolve the task and show what is already on it.
  const loadTaskInvoices = useCallback((taskId) => {
    if (!taskId) { setTaskInvoices(null); return; }
    api.get(`/invoice-intake/task/${taskId}`).then(r => {
      setTaskInvoices(r.data);
      setTarget(t => t && t.task_id === r.data.task_id ? t : { project_id: r.data.project_id, project_name: r.data.project_name, task_id: r.data.task_id, task_title: r.data.task_title, company_names: r.data.company_names });
    }).catch(() => setTaskInvoices(null));
  }, []);
  useEffect(() => { if (presetTaskId) loadTaskInvoices(presetTaskId); }, [presetTaskId, loadTaskInvoices]);
  // review page: load the item for this id (from the list if we have it, else fetch)
  useEffect(() => {
    if (!reviewId) { setReviewState(null); return; }
    const it = items.find(x => x.id === reviewId);
    if (it) { setReviewState(it); return; }
    api.get(`/invoice-intake/${reviewId}`).then(r => setReviewState(r.data)).catch(() => navigate('/accounts/invoices'));
  }, [reviewId]); // eslint-disable-line
  const targetTaskId = target?.task_id;
  useEffect(() => { if (mode === 'single' && targetTaskId) loadTaskInvoices(targetTaskId); }, [mode, targetTaskId, loadTaskInvoices]);

  const uploadFiles = async (files, tgt) => {
    const temp = files.map(fl => ({ tempId: Math.random().toString(36).slice(2), original_name: fl.name, uploading: true, mime_type: fl.type }));
    setItems(prev => [...temp, ...prev]);
    for (let i = 0; i < files.length; i++) {           // one at a time so the list fills progressively
      const fd = new FormData();
      fd.append('files', files[i]);
      if (tgt?.project_id) fd.append('project_id', tgt.project_id);
      if (tgt?.task_id) fd.append('task_id', tgt.task_id);
      try {
        const r = await api.post('/invoice-intake/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const created = r.data[0];
        setItems(prev => prev.map(it => it.tempId === temp[i].tempId ? (created.error ? { ...it, uploading: false, error: created.error } : created) : it));
      } catch (e) {
        setItems(prev => prev.map(it => it.tempId === temp[i].tempId ? { ...it, uploading: false, error: e?.response?.data?.error || e.message } : it));
      }
    }
    loadRecent();
  };

  const replaceItem = (d) => setItems(prev => d.status === 'discarded' ? prev.filter(it => it.id !== d.id) : prev.map(it => it.id === d.id ? { ...it, ...d } : it));
  const discard = async (it) => {
    if (!window.confirm(`Discard ${it.original_name}?`)) return;
    try { await api.delete(`/invoice-intake/${it.id}`); setItems(prev => prev.filter(x => x.id !== it.id)); } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.error, status: 'error' }); }
  };
  const assign = async (it, t) => {
    try { const r = await api.put(`/invoice-intake/${it.id}`, { project_id: t.project_id, task_id: t.task_id }); replaceItem(r.data); setAssigning(null); loadRecent(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.error, status: 'error' }); }
  };
  const targetPicker = (onPick) => <TargetSearch onPick={onPick} recent={recent} compact />;

  const listSource = mode === 'pending' ? pending : items;
  const list = (
    <VStack align="stretch" spacing={2} mt={4}>
      {listSource.map(it => (
        <Flex key={it.id || it.tempId} p={2} bg="white" border="1px solid" borderColor={it.error ? 'red.200' : 'gray.200'} borderRadius="md" align="center" gap={3}
          cursor={it.id && !it.uploading ? 'pointer' : 'default'} onClick={() => it.id && !it.uploading && setReview(it)} _hover={it.id ? { borderColor: 'teal.300' } : {}}>
          <Thumb item={it} />
          <Box flex={1} minW={0}>
            <HStack><Text fontWeight="600" fontSize="sm" isTruncated>{it.original_name}</Text>{statusBadge(it)}</HStack>
            {it.error && <Text fontSize="xs" color="red.600">{it.error}</Text>}
            {it.extraction && (
              <Text fontSize="xs" color="gray.700" mt={0.5}>
                {it.extraction.supplier_name || <i>supplier?</i>} · {it.extraction.invoice_number || it.extraction.order_reference || <i>no number</i>} · {it.extraction.issue_date || <i>no date</i>} ·
                {' '}net {fmtMoney(it.extraction.net)} · VAT {fmtMoney(it.extraction.vat)} · <b>total {fmtMoney(it.extraction.gross)}</b>{it.extraction.supplier_vat_number ? ` · VAT no. ${it.extraction.supplier_vat_number}` : ''}
              </Text>
            )}
            <Text fontSize="xs" color={it.project_id ? 'gray.500' : 'orange.600'} mt={0.5}>
              {it.project_id ? <><FiFolder style={{ display: 'inline' }} /> {it.project_name}{it.task_title ? ` › ${it.task_title}` : ''}</> : (it.id && !it.uploading ? 'No project yet — choose one' : '')}
              {it.invoice_number && it.status === 'approved' ? ` · recorded as ${it.invoice_number}` : ''}
            </Text>
            {(mode === 'multi' || mode === 'pending') && it.id && it.status !== 'approved' && (
              <Box mt={1} onClick={e => e.stopPropagation()}>
                {assigning === it.id
                  ? <Box p={2} bg="gray.50" borderRadius="md"><TargetSearch onPick={(t) => assign(it, t)} recent={recent} compact /><Button size="xs" variant="ghost" mt={1} onClick={() => setAssigning(null)}>Cancel</Button></Box>
                  : <Button size="xs" variant="outline" leftIcon={<FiFolder />} onClick={() => setAssigning(it.id)}>{it.project_id ? 'Change project / task' : 'Choose project / task'}</Button>}
              </Box>
            )}
          </Box>
          <HStack onClick={e => e.stopPropagation()}>
            {it.id && !it.uploading && <Tooltip label={it.status === 'approved' ? 'View' : 'Review & approve'}><IconButton size="sm" variant="ghost" icon={<FiEdit3 />} aria-label="review" onClick={() => setReview(it)} /></Tooltip>}
            {it.id && it.status !== 'approved' && <Tooltip label="Discard"><IconButton size="sm" variant="ghost" colorScheme="red" icon={<FiTrash2 />} aria-label="discard" onClick={() => discard(it)} /></Tooltip>}
          </HStack>
        </Flex>
      ))}
      {listSource.length === 0 && <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>{mode === 'pending' ? 'Nothing waiting — the tray is empty.' : 'Nothing uploaded yet.'}</Text>}
    </VStack>
  );

  if (mode === 'review') {
    return (
      <Box p={3}>
        {review ? (
          <ReviewModal item={review} onClose={() => navigate(returnTo)} targetPicker={targetPicker}
            onSaved={(d) => { replaceItem(d); setReviewState(r => ({ ...r, ...d })); }}
            onApproved={(d) => { replaceItem(d); loadRecent(); navigate(returnTo); }} />
        ) : <Flex justify="center" py={10}><Spinner /></Flex>}
      </Box>
    );
  }

  return (
    <Box p={6} maxW="1100px" mx="auto">
      <HStack mb={5} maxW={!mode ? '440px' : undefined} mx={!mode ? 'auto' : undefined}>
        <IconButton icon={<FiArrowLeft />} aria-label="back" variant="ghost" onClick={() => (mode ? setMode(null) : onBack && onBack())} />
        <Text fontSize="xl" fontWeight="700">Upload Invoices{mode === 'pending' ? ' › Pending' : mode === 'portal' ? ' › Upload link' : mode === 'multi' ? ' › Several targets' : mode === 'single' ? ' › One task' : ''}</Text>
        {mode === 'single' && target && <Badge colorScheme="teal" fontSize="sm"><FiFolder style={{ display: 'inline' }} /> {target.project_name} › {target.task_title}</Badge>}
      </HStack>

      {!mode && (
        <VStack spacing={4} align="stretch" maxW="440px" mx="auto">
          <MenuTile icon={FiUpload} secondary={FiFolder} title="Upload invoices to a specific project and task"
            text="Choose one project and task, then drop one or many invoices — they all go there." onClick={() => setMode('single')} />
          <MenuTile icon={FiUpload} secondary={FiLayers} title="Upload one or more invoices to different projects and tasks"
            text="Drop the invoices first, then choose where each one goes — recently used targets are one click." onClick={() => setMode('multi')} />
          <Box position="relative">
            <MenuTile icon={FiInbox} secondary={FiClock} title="Sort pending uploads"
              text="Receipts and invoices sent in from a phone or an upload link, and anything left with questions unanswered — give each a project and task, answer, approve." onClick={() => { loadPending(); setMode('pending'); }} />
            {pending.length > 0 && <Badge position="absolute" top={3} right={3} colorScheme="orange" fontSize="sm" borderRadius="full" px={2}>{pending.length}</Badge>}
          </Box>
          <MenuTile icon={FiSmartphone} secondary={FiCamera} title="Show my mobile portal"
            text="Your personal upload link and QR code — photograph a receipt on your phone and it lands in your pending tray, attributed to you." onClick={() => navigate('/accounts/invoices/portal')} />
        </VStack>
      )}
      {mode === 'pending' && (<>
        <Text fontSize="xs" color="gray.600" mt={1}>Each item needs a project and task, then review. Items with pending answers reopen the questions when you open them.</Text>
        {list}
      </>)}

      {mode === 'portal' && (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5} maxW="560px">
          <Text fontWeight="700" mb={3}>Receipt upload link</Text>
          <Box>
            {!portal ? <Spinner /> : (
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm">Open this on your phone — or save the QR — to upload a photo of a receipt in two taps. Uploads land in your <b>pending tray</b> for sorting later and are attributed to you.</Text>
                <Flex justify="center"><Image src={portal.qr} alt="QR" w="200px" h="200px" /></Flex>
                <HStack><Input size="sm" value={portal.url} isReadOnly fontFamily="mono" fontSize="11px" /><Button size="sm" onClick={() => { navigator.clipboard?.writeText(portal.url); toast({ title: 'Link copied', status: 'success', duration: 1500 }); }}>Copy</Button></HStack>
                <Text fontSize="xs" color="gray.500">Used {portal.upload_count || 0} time{portal.upload_count === 1 ? '' : 's'}{portal.last_used_at ? `, last ${new Date(portal.last_used_at).toLocaleString('en-GB')}` : ''}. Access for all links in your organisation: <b>{portal.access_mode}</b>.</Text>
                <Button size="xs" variant="link" colorScheme="red" alignSelf="flex-start" onClick={async () => { if (!window.confirm('Create a new link? The old one will stop working.')) return; await api.post('/invoice-intake/portals/rotate'); openPortal(); }}>Replace this link</Button>
                {portalSettings && user?.role === 'admin' && (
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Text fontSize="xs" fontWeight="700" mb={1}>Organisation setting (admin)</Text>
                    <HStack>
                      <Select size="sm" value={portalSettings.access_mode} onChange={e => setPortalSettings(ps => ({ ...ps, access_mode: e.target.value }))}>
                        <option value="open">Open — anyone with a link can upload</option>
                        <option value="pin">PIN — one PIN for the organisation</option>
                        <option value="login">Sign in — ForgeBOS account required</option>
                      </Select>
                    </HStack>
                    {portalSettings.access_mode === 'pin' && <Input mt={2} size="sm" placeholder={portalSettings.pin_set ? 'New PIN (leave blank to keep)' : 'Set a 4–8 digit PIN'} value={portalSettings.pin || ''} onChange={e => setPortalSettings(ps => ({ ...ps, pin: e.target.value.replace(/\D/g, '') }))} />}
                    <Button mt={2} size="sm" colorScheme="teal" onClick={async () => { try { await api.put('/invoice-intake/portals/settings', { access_mode: portalSettings.access_mode, pin: portalSettings.pin || undefined }); toast({ title: 'Saved', status: 'success', duration: 1500 }); openPortal(); } catch (e) { toast({ title: e?.response?.data?.error || 'Failed', status: 'error', duration: 3000 }); } }}>Save</Button>
                    <Text fontSize="10px" color="gray.500" mt={1}>Links are long random tokens that reveal nothing about the organisation, project or task. Archives (zip) are never accepted.</Text>
                  </Box>
                )}
              </VStack>
            )}
          </Box>
        </Box>
      )}

      {mode === 'single' && !target && (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5} maxW="640px">
          <Text fontWeight="600" mb={3}>Which project and task?</Text>
          <TargetSearch onPick={setTarget} recent={recent} />
        </Box>
      )}
      {mode === 'single' && target && (<>
        {taskInvoices && target?.task_id === taskInvoices.task_id && (
          <Box mb={3} p={3} border="1px solid" borderColor="red.100" bg="red.50" borderRadius="md">
            <HStack justify="space-between" mb={1}>
              <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color="red.700">Supplier invoices on this task</Text>
              <Text fontSize="xs" color="gray.600">{taskInvoices.company_names ? `${taskInvoices.company_names} › ` : ''}{taskInvoices.project_name} › {taskInvoices.task_title}</Text>
            </HStack>
            {taskInvoices.invoices.length === 0 ? <Text fontSize="xs" color="gray.600">None recorded yet — drop an invoice below.</Text> : (
              <Box overflowX="auto">
                <Table size="sm" variant="simple" minW="760px">
                  <Thead><Tr><Th>Supplier</Th><Th>Invoice</Th><Th>Date</Th><Th>Status</Th><Th isNumeric>Net</Th><Th isNumeric>VAT</Th><Th isNumeric>Gross</Th><Th isNumeric>Claimable VAT</Th><Th></Th></Tr></Thead>
                  <Tbody>
                    {taskInvoices.invoices.map(inv => (
                      <Tr key={inv.invoice_id}>
                        <Td fontSize="xs">{inv.supplier_name || '—'}</Td>
                        <Td fontSize="xs">{inv.invoice_number || '—'}{inv.allocated && <Badge ml={1} colorScheme="purple" fontSize="8px">{inv.is_own ? 'shared with other tasks' : 'share of another task\u2019s invoice'}</Badge>}</Td>
                        <Td fontSize="xs">{inv.issue_date ? String(inv.issue_date).slice(0, 10) : '—'}</Td>
                        <Td fontSize="xs">{String(inv.status || '').replace(/^recv_/, '').replace(/_/g, ' ')}</Td>
                        <Td isNumeric fontSize="xs" color="red.700" fontWeight="600">{inv.task_net.toFixed(2)}</Td>
                        <Td isNumeric fontSize="xs" color="red.700">{inv.task_vat.toFixed(2)}</Td>
                        <Td isNumeric fontSize="xs" color="red.700" fontWeight="700">{inv.task_gross.toFixed(2)}</Td>
                        <Td isNumeric fontSize="xs">{inv.claimable_vat != null ? (+inv.claimable_vat).toFixed(2) : '—'}{inv.vat_treatment && inv.vat_treatment !== 'standard' ? <Text as="span" color="gray.500"> ({inv.vat_treatment.replace('_', ' ')})</Text> : null}</Td>
                        <Td>{inv.intake_id && <Button size="xs" variant="link" onClick={() => { const it = items.find(x => x.id === inv.intake_id); if (it) setReview(it); else api.get(`/invoice-intake/${inv.intake_id}`).then(r => setReview(r.data)).catch(() => {}); }}>open</Button>}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <Text fontSize="10px" color="gray.500" mt={1}>Amounts in red are what this task carries: the whole invoice, or — where lines were allocated across tasks — this task's lines plus its pro-rata share of delivery.</Text>
              </Box>
            )}
          </Box>
        )}
        <DropZone onFiles={(files) => uploadFiles(files, target)} />
        <HStack mt={2}><Button size="xs" variant="link" onClick={() => setTarget(null)}>Change project / task</Button></HStack>
        {list}
      </>)}
      {mode === 'multi' && (<>
        <DropZone onFiles={(files) => uploadFiles(files, null)} />
        <Text fontSize="xs" color="gray.600" mt={2}>Each invoice below needs a project and task before it can be approved — recently used ones are offered first.</Text>
        {list}
      </>)}

    </Box>
  );
}
